import React, { useState } from 'react';
import { Button } from './Button';
import { EditIcon, MoreVerticalIcon, ClockIcon, PlayIcon, PauseIcon, HistoryIcon, EyeIcon, CheckCircleIcon, DownloadIcon, UploadIcon, CopyIcon, SendIcon } from 'lucide-react';
import { EndpointHistoryModal } from './modals/EndpointHistoryModal';
interface Endpoint {
  id: number;
  path: string;
  createdOn: string;
  lastUpdated: string;
  status: 'In-Progress' | 'Ready for Approval' | 'Suspended' | 'Cloned';
  tenantId: string;
  workflowStatus?: 'active' | 'paused';
  type?: 'Push' | 'Pull';
}
interface EndpointTableProps {
  endpoints: Endpoint[];
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onClone?: (id: number) => void;
  onForwardForApproval?: (id: number) => void;
  showStatusColumn?: boolean;
  createdTimeLabel?: string;
  showTypeColumn?: boolean;
  showActionsColumn?: boolean;
}
export const EndpointTable: React.FC<EndpointTableProps> = ({
  endpoints,
  onEdit,
  onDelete,
  onClone,
  onForwardForApproval,
  showStatusColumn = true,
  createdTimeLabel = 'Created Time',
  showTypeColumn = false,
  showActionsColumn = true
}) => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState<number | null>(null);
  const getStatusColor = (status: Endpoint['status']) => {
    switch (status) {
      case 'Ready for Approval':
        return 'bg-green-100 text-green-800';
      case 'In-Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      case 'Cloned':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: Endpoint['status']) => {
    switch (status) {
      case 'Ready for Approval':
        return <CheckCircleIcon size={12} className="mr-1" data-id="element-817" />;
      case 'In-Progress':
        return <ClockIcon size={12} className="mr-1" data-id="element-818" />;
      case 'Suspended':
        return <PauseIcon size={12} className="mr-1" data-id="element-819" />;
      case 'Cloned':
        return <CopyIcon size={12} className="mr-1" data-id="element-820" />;
      default:
        return null;
    }
  };
  const getTypeIcon = (type?: 'Push' | 'Pull') => {
    switch (type) {
      case 'Push':
        return <UploadIcon size={14} className="mr-1 text-blue-500" data-id="element-821" />;
      case 'Pull':
        return <DownloadIcon size={14} className="mr-1 text-purple-500" data-id="element-822" />;
      default:
        return null;
    }
  };
  const handleAction = (action: string, endpoint: Endpoint) => {
    switch (action) {
      case 'history':
        setShowHistory(endpoint.id);
        break;
      case 'forward':
        if (onForwardForApproval) {
          onForwardForApproval(endpoint.id);
        }
        break;
      default:
        console.log('Action:', action, 'Endpoint:', endpoint.id);
    }
    setActiveDropdown(null);
  };
  return <div className="overflow-x-auto bg-white rounded-lg shadow" data-id="element-823">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-824">
        <thead className="bg-gray-50" data-id="element-825">
          <tr data-id="element-826">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-827">
              Endpoint Path
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-828">
              Tenant ID
            </th>
            {showTypeColumn && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-829">
                Type
              </th>}
            {showStatusColumn && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-830">
                Status
              </th>}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-831">
              {createdTimeLabel}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-832">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-833">
          {endpoints.length > 0 ? endpoints.map(endpoint => <tr key={endpoint.id} className="hover:bg-gray-50" data-id="element-834">
                <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-835">
                  <div className="flex items-center" data-id="element-836">
                    <span className="font-medium text-gray-900" data-id="element-837">
                      {endpoint.path}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-838">
                  <div className="flex items-center" data-id="element-839">
                    <span className="font-medium" data-id="element-840">
                      {endpoint.tenantId || 'default'}
                    </span>
                  </div>
                </td>
                {showTypeColumn && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-841">
                    <div className="flex items-center" data-id="element-842">
                      {getTypeIcon(endpoint.type)}
                      <span className="font-medium" data-id="element-843">
                        {endpoint.type || 'N/A'}
                      </span>
                    </div>
                  </td>}
                {showStatusColumn && <td className="px-6 py-4 whitespace-nowrap" data-id="element-844">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(endpoint.status)}`} data-id="element-845">
                      {getStatusIcon(endpoint.status)}
                      {endpoint.status}
                    </span>
                  </td>}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-846">
                  <div className="flex items-center" data-id="element-847">
                    <ClockIcon size={16} className="mr-1 text-gray-400" data-id="element-848" />
                    {new Date(endpoint.createdOn).toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-849">
                  <div className="flex items-center space-x-2" data-id="element-850">
                    <Button variant="secondary" size="sm" icon={<EyeIcon size={14} data-id="element-852" />} onClick={() => onEdit(endpoint.id)} data-id="element-851">
                      View
                    </Button>
                    {showActionsColumn && <div className="relative" data-id="element-853">
                        <Button variant="primary" size="sm" onClick={() => setActiveDropdown(activeDropdown === endpoint.id ? null : endpoint.id)} icon={<MoreVerticalIcon size={14} data-id="element-855" />} data-id="element-854">
                          Actions
                        </Button>
                        {activeDropdown === endpoint.id && <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-856">
                            <div className="py-1" role="menu" data-id="element-857">
                              <button onClick={() => handleAction('history', endpoint)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-858">
                                <HistoryIcon size={14} className="mr-2" data-id="element-859" />
                                View History
                              </button>
                              {endpoint.status === 'In-Progress' && <button onClick={() => handleAction('forward', endpoint)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-860">
                                  <SendIcon size={14} className="mr-2" data-id="element-861" />
                                  Forward for Approval
                                </button>}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </td>
              </tr>) : <tr data-id="element-862">
              <td colSpan={showStatusColumn ? showTypeColumn ? 6 : 5 : showTypeColumn ? 5 : 4} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-863">
                No endpoints found
              </td>
            </tr>}
        </tbody>
      </table>
      {showHistory !== null && <EndpointHistoryModal endpointId={showHistory} onClose={() => setShowHistory(null)} data-id="element-864" />}
    </div>;
};